import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArrowLeft, Circle, Copy, History, Pencil, Play, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import RunDemoModal from "@/components/RunDemoModal";
import RecentActivityWidget from "@/components/RecentActivityWidget";
import AIStepGenerationPanel from "@/components/AIStepGenerationPanel";
import { workflowRef } from "@/lib/workflowNumber";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
}

export default function WorkflowDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeVersion, setActiveVersion] = useState(null);
  const [runOpen, setRunOpen] = useState(false);
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("workflow.write");
  const canRun = hasPermission("workflow.run");

  const wf = useQuery({
    queryKey: ["workflow", id],
    queryFn: async () => (await api.get(`/workflows/${id}`)).data,
  });
  const versions = useQuery({
    queryKey: ["versions", id],
    queryFn: async () => (await api.get(`/workflows/${id}/versions`)).data,
  });
  const versionDetail = useQuery({
    queryKey: ["version", id, activeVersion],
    queryFn: async () =>
      (await api.get(`/workflows/${id}/versions/${activeVersion}`)).data,
    enabled: activeVersion != null,
  });

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: ["workflow", id] });
    queryClient.invalidateQueries({ queryKey: ["workflows"] });
    queryClient.invalidateQueries({ queryKey: ["versions", id] });
  }

  const archiveMutation = useMutation({
    mutationFn: async () => (await api.post(`/workflows/${id}/archive`)).data,
    onSuccess: () => {
      toast.success("Workflow archived");
      invalidateAll();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });
  const unarchiveMutation = useMutation({
    mutationFn: async () => (await api.post(`/workflows/${id}/unarchive`)).data,
    onSuccess: () => {
      toast.success("Workflow restored");
      invalidateAll();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });
  const duplicateMutation = useMutation({
    mutationFn: async () => (await api.post(`/workflows/${id}/duplicate`)).data,
    onSuccess: (data) => {
      toast.success(`Duplicated as "${data.name}"`);
      navigate(`/workflows/${data.workflow_id}`);
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  if (wf.isLoading) return <Skeleton className="h-96 w-full rounded-xl" />;
  if (wf.isError) {
    return (
      <div
        className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        data-testid="workflow-detail-error"
      >
        {formatApiError(wf.error)}
      </div>
    );
  }

  const d = wf.data;
  const versionShown =
    activeVersion == null ? null : versionDetail.data;
  const displayedSteps =
    versionShown?.steps ?? d.steps ?? [];
  const displayedVars =
    versionShown?.variables ?? d.variables ?? [];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-6" data-testid="workflow-detail-page">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <Link
              to="/workflows"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900"
              data-testid="detail-back-link"
            >
              <ArrowLeft size={14} />
              Back to library
            </Link>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <span
                className="rounded-md border border-slate-300 bg-slate-50 px-2 py-0.5 font-mono text-lg font-semibold tracking-tight text-slate-700"
                data-testid="detail-workflow-number"
              >
                {workflowRef(d.workflow_number)}
              </span>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                {d.name}
              </h1>
              <StatusPill status={d.status} />
              <span className="font-mono text-sm text-slate-500" data-testid="detail-version">
                v{d.current_version}
              </span>
            </div>
            {d.description && (
              <p className="mt-2 max-w-2xl text-sm text-slate-600" data-testid="detail-description">
                {d.description}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-400">
              <span>
                Slug: <code className="font-mono">{d.slug}</code>
              </span>
              <span>·</span>
              <span>Updated {formatDate(d.updated_at)}</span>
              <span>·</span>
              <span>Created by {d.created_by}</span>
            </div>
            {d.tags?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {d.tags.map((t) => (
                  <Badge
                    key={t}
                    variant="outline"
                    className="rounded-full border-indigo-200 bg-indigo-50 text-[10px] font-medium text-indigo-700"
                  >
                    {t}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="default"
                    disabled={!canRun || d.status !== "active"}
                    onClick={() => canRun && setRunOpen(true)}
                    data-testid="detail-run-button"
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300"
                  >
                    <Circle size={11} fill="currentColor" className="text-rose-300" />
                    Generate Video
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {!canRun
                  ? "Requires presenter role"
                  : d.status !== "active"
                  ? "Archived workflows can't be run"
                  : "Open the Generate Video dialog"}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="outline"
                    disabled={!canWrite}
                    onClick={() => canWrite && duplicateMutation.mutate()}
                    data-testid="detail-duplicate-button"
                    className="gap-2"
                  >
                    <Copy size={14} />
                    Duplicate
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{canWrite ? "Duplicate" : "Requires builder role"}</TooltipContent>
            </Tooltip>
            {d.status === "active" ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="outline"
                      disabled={!canWrite}
                      onClick={() => canWrite && archiveMutation.mutate()}
                      data-testid="detail-archive-button"
                      className="gap-2 text-slate-700"
                    >
                      <Archive size={14} />
                      Archive
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{canWrite ? "Archive" : "Requires builder role"}</TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="outline"
                      disabled={!canWrite}
                      onClick={() => canWrite && unarchiveMutation.mutate()}
                      data-testid="detail-unarchive-button"
                      className="gap-2"
                    >
                      <RotateCcw size={14} />
                      Unarchive
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{canWrite ? "Unarchive" : "Requires builder role"}</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={() => canWrite && navigate(`/workflows/${id}/edit`)}
                    disabled={!canWrite}
                    data-testid="detail-edit-button"
                    className="gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300"
                  >
                    <Pencil size={14} />
                    Edit
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{canWrite ? "Edit" : "Requires builder role"}</TooltipContent>
            </Tooltip>
          </div>
        </header>

        {activeVersion != null && (
          <div
            className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800"
            data-testid="viewing-old-version-banner"
          >
            <span>
              Showing <span className="font-mono font-semibold">v{activeVersion}</span> (historical
              snapshot — read-only).
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveVersion(null)}
              data-testid="back-to-latest-button"
            >
              Back to latest
            </Button>
          </div>
        )}

        {/* R3 Phase 6A.5 — AI step generation surface.  The panel
            self-hides for viewer-role and archived workflows. */}
        {activeVersion == null && (
          <AIStepGenerationPanel workflow={d} />
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Card title={`Steps · ${displayedSteps.length}`}>
              {displayedSteps.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="detail-steps-empty">
                  No steps yet.
                </p>
              ) : (
                <ol className="space-y-2" data-testid="detail-steps-list">
                  {displayedSteps.map((s, i) => (
                    <li
                      key={s.id || i}
                      className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3"
                      data-testid={`detail-step-${i}`}
                    >
                      <span className="grid h-7 w-7 flex-none place-items-center rounded-md bg-indigo-50 font-mono text-xs font-semibold text-indigo-700">
                        {s.order || i + 1}
                      </span>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-slate-800">{s.name}</span>
                          <Badge
                            variant="outline"
                            className="rounded-full border-slate-200 bg-slate-50 text-[10px] font-medium text-slate-600"
                          >
                            {s.action}
                          </Badge>
                          {s.continue_on_failure && (
                            <Badge
                              variant="outline"
                              className="rounded-full border-amber-200 bg-amber-50 text-[10px] font-medium text-amber-700"
                            >
                              continue-on-failure
                            </Badge>
                          )}
                        </div>
                        <div className="mt-1 grid grid-cols-1 gap-x-6 text-[11px] text-slate-500 sm:grid-cols-2">
                          {s.url && <span className="truncate">URL: <code className="font-mono">{s.url}</code></span>}
                          {s.selector && (
                            <span className="truncate">
                              {s.selector_strategy || "css"}: <code className="font-mono">{s.selector}</code>
                            </span>
                          )}
                          {s.value && <span className="truncate">value: <code className="font-mono">{s.value}</code></span>}
                          {s.wait_ms != null && (s.action === "wait" || s.action === "settle_delay") && (
                            <span>wait_ms: <code className="font-mono">{s.wait_ms}</code></span>
                          )}
                          {s.screenshot_name && (
                            <span>screenshot: <code className="font-mono">{s.screenshot_name}</code></span>
                          )}
                          {s.assertion?.expected && (
                            <span>
                              {s.assertion.type}: <code className="font-mono">{s.assertion.expected}</code>
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </Card>

            <Card title={`Variables · ${displayedVars.length}`}>
              {displayedVars.length === 0 ? (
                <p className="text-sm text-slate-400">No variables.</p>
              ) : (
                <Table data-testid="detail-variables-table">
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Key</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Label</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Default</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Required</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedVars.map((v) => (
                      <TableRow key={v.key}>
                        <TableCell><code className="font-mono text-xs">{v.key}</code></TableCell>
                        <TableCell className="text-sm text-slate-700">{v.label}</TableCell>
                        <TableCell className="text-sm text-slate-600">{v.default || <span className="text-slate-400">—</span>}</TableCell>
                        <TableCell>{v.required ? "Yes" : "No"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </div>

          <Card
            title={
              <span className="flex items-center gap-2">
                <History size={16} className="text-indigo-600" />
                Versions
              </span>
            }
          >
            {versions.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : versions.isError ? (
              <p className="text-sm text-red-600">{formatApiError(versions.error)}</p>
            ) : (
              <ul className="space-y-2" data-testid="versions-list">
                {(versions.data || []).map((v) => {
                  const isLatest = v.version === d.current_version;
                  const isActive = activeVersion === v.version;
                  return (
                    <li key={v.version}>
                      <button
                        type="button"
                        onClick={() => setActiveVersion(isLatest ? null : v.version)}
                        data-testid={`version-row-${v.version}`}
                        className={[
                          "flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                          isActive
                            ? "border-indigo-300 bg-indigo-50 text-indigo-800"
                            : "border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40",
                        ].join(" ")}
                      >
                        <div>
                          <div className="font-mono font-semibold">v{v.version}</div>
                          <div className="text-[11px] text-slate-500">
                            {v.step_count} step{v.step_count === 1 ? "" : "s"} · {v.created_by} · {formatDate(v.created_at)}
                          </div>
                        </div>
                        {isLatest && (
                          <Badge className="bg-indigo-600 text-[10px]">Latest</Badge>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
          <RecentActivityWidget
            workflowId={id}
            limit={10}
            title="Recent activity"
            emptyHint="No audit events for this workflow yet."
            compact={true}
          />
          <WorkflowCostRollupCard workflowId={id} />
        </div>
      </div>
      <RunDemoModal
        workflow={d}
        open={runOpen}
        onOpenChange={setRunOpen}
      />
    </TooltipProvider>
  );
}

function Card({ title, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold tracking-tight text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

function WorkflowCostRollupCard({ workflowId }) {
  // R3 — minimal rollup card.  Three lines.  Reuses the standard <Card />
  // chrome on the page so visual weight matches the Versions card next to it.
  const q = useQuery({
    queryKey: ["workflow-cost-rollup", workflowId],
    queryFn: async () => (await api.get(`/workflows/${workflowId}/cost-rollup`)).data,
    enabled: !!workflowId,
    staleTime: 30_000,
  });
  const fmt = (n) => `$${Number(n || 0).toFixed(4)}`;
  return (
    <section
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
      data-testid="workflow-cost-rollup-card"
    >
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-900">
        <span className="text-emerald-600">$</span>
        Cost rollup
      </h2>
      {q.isLoading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : q.isError ? (
        <p className="text-xs text-rose-600">{formatApiError(q.error)}</p>
      ) : (
        <dl
          className="space-y-1 text-xs text-slate-600"
          data-testid="workflow-cost-rollup-body"
        >
          <div className="flex items-baseline justify-between">
            <span>Avg per generation</span>
            <span
              className="font-mono text-slate-900"
              data-testid="workflow-cost-rollup-avg"
            >
              {fmt(q.data.avg_per_generation_usd)}
            </span>
          </div>
          <div className="flex items-baseline justify-between">
            <span>Total spend</span>
            <span
              className="font-mono text-slate-900"
              data-testid="workflow-cost-rollup-total"
            >
              {fmt(q.data.total_spend_usd)}
              <span className="ml-1 text-[10px] text-slate-400">
                ({q.data.completed_runs} run{q.data.completed_runs === 1 ? "" : "s"})
              </span>
            </span>
          </div>
          <div className="flex items-baseline justify-between">
            <span>Last 30 days</span>
            <span
              className="font-mono text-slate-900"
              data-testid="workflow-cost-rollup-30d"
            >
              {fmt(q.data.last_30d_spend_usd)}
            </span>
          </div>
        </dl>
      )}
    </section>
  );
}

function StatusPill({ status }) {
  const isArchived = status === "archived";
  return (
    <span
      data-testid={`detail-status-${status}`}
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium",
        isArchived ? "bg-slate-100 text-slate-600" : "bg-emerald-50 text-emerald-700",
      ].join(" ")}
    >
      <span className={["h-1.5 w-1.5 rounded-full", isArchived ? "bg-slate-400" : "bg-emerald-500"].join(" ")} />
      {isArchived ? "Archived" : "Active"}
    </span>
  );
}
